import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  LinearProgress,
  Alert,
  Button,
  Snackbar,
  Stack,
  Paper,
  useTheme,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Speed as SpeedIcon,
  AccountBalanceWallet as WalletIcon,
  Warning as WarningIcon,
  Lightbulb as LightbulbIcon,
  Refresh as RefreshIcon,
  Memory as MemoryIcon,
  ChatBubbleOutline as ChatIcon,
  Token as TokenIcon,
  AttachMoney as MoneyIcon,
  Assessment as ChartIcon,
  AccessTime as TimeIcon,
  BarChart as BarChartIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { agentsService } from '../services/agentsService';
import { motion } from 'framer-motion';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, Legend, ResponsiveContainer } from 'recharts';

const AgentDiagnosticsV5 = ({ agentId, agent }) => {
  const theme = useTheme();
  const [diagnostics, setDiagnostics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [selectedPeriod, setSelectedPeriod] = useState('7d');

  const periodOptions = [
    { value: '7d', label: '7 dias' },
    { value: '1m', label: '1 mês' },
    { value: '6m', label: '6 meses' },
    { value: '1y', label: '1 ano' },
  ];

  useEffect(() => {
    if (agentId) {
      loadDiagnostics();
    }
  }, [agentId, selectedPeriod]);

  const loadDiagnostics = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await agentsService.getAgentDiagnostics(agentId, selectedPeriod);
      setDiagnostics(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleInvalidateCache = async () => {
    try {
      await agentsService.invalidateCache();
      setSnackbar({
        open: true,
        message: 'Cache invalidado com sucesso!',
        severity: 'success'
      });
      setTimeout(loadDiagnostics, 1000);
    } catch (err) {
      setSnackbar({
        open: true,
        message: 'Erro ao invalidar cache: ' + err.message,
        severity: 'error'
      });
    }
  };

  const formatNumber = (num) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num?.toString() || '0';
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 2,
    }).format(value || 0);
  };

  const formatCredits = (credits) => {
    return `${credits?.toFixed(1) || '0.0'}`;
  };

  const getChartData = () => {
    if (!diagnostics?.usage_trends) return [];
    
    // Se for período de 7 dias, usar dados diários
    if (selectedPeriod === '7d') {
      const dailyStats = diagnostics.usage_trends.daily_stats || {};
      return Object.entries(dailyStats)
        .sort(([a], [b]) => a.localeCompare(b))
        .slice(-7) // Últimos 7 dias
        .map(([date, data]) => ({
          period: new Date(date).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' }),
          conversas: data.unique_sessions || 0,
          interacoes: data.interactions || 0,
          creditos: data.credits_used || 0,
          valor: data.revenue_brl || 0
        }));
    }

    // Para outros períodos, usar dados mensais
    const monthlyStats = diagnostics.usage_trends.monthly_stats || {};
    return Object.entries(monthlyStats)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([month, data]) => ({
        period: new Date(month + '-01').toLocaleDateString('pt-BR', { month: 'short', year: 'numeric' }),
        conversas: data.unique_sessions || 0,
        interacoes: data.interactions || 0,
        creditos: data.credits_used || 0,
        valor: data.revenue_brl || 0
      }));
  };

  const benchmarkTooltips = {
    performance: "Benchmarks de Performance:\n• Latência Ideal: ≤ 3s\n• Latência Elevada: 5-10s\n• Latência Crítica: > 10s\n• Tokens Ideal: ≤ 1500/interação\n• Tokens Alto: > 3000/interação",
    recommendations: "Recomendações baseadas em:\n• Análise de custos por interação\n• Padrões de latência da indústria\n• Otimização de consumo de tokens\n• Gestão de saldo de créditos"
  };

  if (loading) {
    return (
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            <SpeedIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            Diagnóstico do Agente
          </Typography>
          <LinearProgress sx={{ mb: 2 }} />
          <Typography variant="body2" color="text.secondary">
            Analisando performance e custos...
          </Typography>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Alert severity="error" sx={{ mb: 2 }}>
            Erro ao carregar diagnóstico: {error}
          </Alert>
          <Button variant="outlined" onClick={loadDiagnostics} startIcon={<RefreshIcon />}>
            Tentar novamente
          </Button>
        </CardContent>
      </Card>
    );
  }

  const performanceStats = diagnostics?.performance_stats || {};
  const costStats = diagnostics?.cost_stats || {};
  const warnings = diagnostics?.warnings || {};
  const recommendations = diagnostics?.recommendations || [];
  const chartData = getChartData();

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      {/* Header com Seletor de Período e Botão Atualizar */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
          Diagnóstico do Agente
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Período</InputLabel>
            <Select
              value={selectedPeriod}
              label="Período"
              onChange={(e) => setSelectedPeriod(e.target.value)}
            >
              {periodOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Tooltip title="Atualizar Diagnóstico">
            <IconButton 
              onClick={loadDiagnostics}
              color="primary"
              sx={{ 
                bgcolor: 'primary.50',
                '&:hover': { bgcolor: 'primary.100' }
              }}
            >
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          {!diagnostics?.cache_info?.is_cached && (
            <Tooltip title="Recarregar na Memória">
              <IconButton 
                onClick={handleInvalidateCache}
                color="secondary"
                sx={{ 
                  bgcolor: 'secondary.50',
                  '&:hover': { bgcolor: 'secondary.100' }
                }}
              >
                <MemoryIcon />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>

      {/* Layout em Duas Colunas */}
      <Grid container spacing={3}>
        {/* COLUNA ESQUERDA */}
        <Grid item xs={12} lg={8}>
          {/* Métricas Principais em Card */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 3, fontWeight: 'bold' }}>
                Métricas Principais
              </Typography>
              <Grid container spacing={2}>
                {/* Interações */}
                <Grid item xs={6} md={6}>
                  <Card sx={{ 
                    height: '120px', 
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', 
                    color: 'white',
                    borderRadius: 2
                  }}>
                    <CardContent sx={{ textAlign: 'center', py: 2 }}>
                      <ChatIcon sx={{ fontSize: 32, mb: 1, opacity: 0.9 }} />
                      <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                        {formatNumber(performanceStats.total_interactions)}
                      </Typography>
                      <Typography variant="body2" sx={{ opacity: 0.9, fontSize: '0.875rem' }}>
                        Interações
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Latência */}
                <Grid item xs={6} md={6}>
                  <Card sx={{ 
                    height: '120px', 
                    background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', 
                    color: 'white',
                    borderRadius: 2
                  }}>
                    <CardContent sx={{ textAlign: 'center', py: 2 }}>
                      <TimeIcon sx={{ fontSize: 32, mb: 1, opacity: 0.9 }} />
                      <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                        {performanceStats.avg_latency_seconds?.toFixed(1) || '0.0'}s
                      </Typography>
                      <Typography variant="body2" sx={{ opacity: 0.9, fontSize: '0.875rem' }}>
                        Latência Média
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Valor Total */}
                <Grid item xs={6} md={6}>
                  <Card sx={{ 
                    height: '120px', 
                    background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', 
                    color: 'white',
                    borderRadius: 2
                  }}>
                    <CardContent sx={{ textAlign: 'center', py: 2 }}>
                      <MoneyIcon sx={{ fontSize: 32, mb: 1, opacity: 0.9 }} />
                      <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 0.5, fontSize: '1.5rem' }}>
                        {formatCurrency(costStats.total_revenue_brl)}
                      </Typography>
                      <Typography variant="body2" sx={{ opacity: 0.9, fontSize: '0.875rem' }}>
                        Valor Total
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Créditos Utilizados */}
                <Grid item xs={6} md={6}>
                  <Card sx={{ 
                    height: '120px', 
                    background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)', 
                    color: 'white',
                    borderRadius: 2
                  }}>
                    <CardContent sx={{ textAlign: 'center', py: 2 }}>
                      <TokenIcon sx={{ fontSize: 32, mb: 1, opacity: 0.9 }} />
                      <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                        {formatCredits(costStats.total_credits_used)}
                      </Typography>
                      <Typography variant="body2" sx={{ opacity: 0.9, fontSize: '0.875rem' }}>
                        Créditos Usados
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Métricas de Performance */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
                <ChartIcon sx={{ mr: 1 }} />
                Métricas de Performance
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={4}>
                  <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'grey.50' }}>
                    <Typography variant="h5" color="primary" sx={{ fontWeight: 'bold' }}>
                      {formatCredits(Math.round(performanceStats.avg_tokens_per_interaction * 0.1 || 0))}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Créditos por Interação
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={4}>
                  <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'grey.50' }}>
                    <Typography variant="h5" color="secondary" sx={{ fontWeight: 'bold' }}>
                      {formatCredits(costStats.total_credits_used)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Total de Créditos
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={4}>
                  <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'grey.50' }}>
                    <Typography variant="h5" color="info.main" sx={{ fontWeight: 'bold' }}>
                      {diagnostics?.usage_trends?.total_days_active || 0}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Dias com Atividade
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

        </Grid>

        {/* COLUNA DIREITA */}
        <Grid item xs={12} lg={4}>
          {/* Saldo Atual com Título */}
          <Card sx={{ 
            mb: 3,
            border: '2px solid', 
            borderColor: costStats.current_balance_cr < 10 ? 'error.main' : costStats.current_balance_cr < 50 ? 'warning.main' : 'success.main',
            bgcolor: costStats.current_balance_cr < 10 ? 'error.50' : costStats.current_balance_cr < 50 ? 'warning.50' : 'success.50'
          }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
                <WalletIcon sx={{ mr: 1 }} />
                Créditos Disponíveis
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ textAlign: 'center', flex: 1 }}>
                  <Typography variant="h3" sx={{ 
                    fontWeight: 'bold',
                    color: costStats.current_balance_cr < 10 ? 'error.main' : costStats.current_balance_cr < 50 ? 'warning.main' : 'success.main'
                  }}>
                    {formatCredits(costStats.current_balance_cr)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Saldo Atual
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>

          {/* Alertas */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
                <WarningIcon color="warning" sx={{ mr: 1 }} />
                Alertas de Performance
                <Tooltip title={benchmarkTooltips.performance} arrow>
                  <InfoIcon sx={{ ml: 1, fontSize: 18, color: 'text.secondary', cursor: 'help' }} />
                </Tooltip>
              </Typography>
              
              {[...warnings.performance || [], ...warnings.cost || []].length === 0 ? (
                <Alert severity="success" variant="outlined">
                  Todos os indicadores estão dentro dos padrões esperados
                </Alert>
              ) : (
                <Stack spacing={1}>
                  {warnings.performance?.map((warning, index) => (
                    <Alert key={`perf-${index}`} severity="warning" variant="outlined" sx={{ fontSize: '0.875rem' }}>
                      {warning}
                    </Alert>
                  ))}
                  {warnings.cost?.map((warning, index) => (
                    <Alert key={`cost-${index}`} severity="error" variant="outlined" sx={{ fontSize: '0.875rem' }}>
                      {warning}
                    </Alert>
                  ))}
                </Stack>
              )}
            </CardContent>
          </Card>

          {/* Recomendações */}
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
                <LightbulbIcon color="info" sx={{ mr: 1 }} />
                Recomendações
                <Tooltip title={benchmarkTooltips.recommendations} arrow>
                  <InfoIcon sx={{ ml: 1, fontSize: 18, color: 'text.secondary', cursor: 'help' }} />
                </Tooltip>
              </Typography>
              
              {recommendations.length === 0 ? (
                <Alert severity="info" variant="outlined">
                  Nenhuma recomendação específica no momento
                </Alert>
              ) : (
                <Stack spacing={1}>
                  {recommendations.map((rec, index) => (
                    <Alert key={index} severity="info" variant="outlined" sx={{ fontSize: '0.875rem' }}>
                      {rec}
                    </Alert>
                  ))}
                </Stack>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Gráficos de Tendência - Largura Total com Espaço Adequado */}
      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 3, fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
            <BarChartIcon sx={{ mr: 1 }} />
            Tendência de Uso
          </Typography>
          
          {chartData.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
              Dados insuficientes para gerar gráfico de tendência
            </Typography>
          ) : (
            <Grid container spacing={3}>
              {/* Card do Gráfico de Conversas e Interações */}
              <Grid item xs={12} md={6}>
                <Card sx={{ height: '100%', border: '1px solid', borderColor: 'grey.200' }}>
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold', textAlign: 'center', mb: 2 }}>
                      Conversas e Interações
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                      <ResponsiveContainer width="100%" height={350}>
                        <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 40 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                          <XAxis 
                            dataKey="period" 
                            tick={{ fontSize: 12 }}
                            tickLine={{ stroke: '#ccc' }}
                            angle={chartData.length > 6 ? -45 : 0}
                            textAnchor={chartData.length > 6 ? 'end' : 'middle'}
                            height={chartData.length > 6 ? 60 : 30}
                          />
                          <YAxis 
                            tick={{ fontSize: 12 }}
                            tickLine={{ stroke: '#ccc' }}
                          />
                          <RechartsTooltip 
                            contentStyle={{ 
                              backgroundColor: '#fff', 
                              border: '1px solid #ccc',
                              borderRadius: '8px',
                              boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                            }}
                          />
                          <Legend />
                          <Bar 
                            dataKey="conversas" 
                            fill={theme.palette.primary.main} 
                            name="Conversas"
                            radius={[4, 4, 0, 0]}
                          />
                          <Bar 
                            dataKey="interacoes" 
                            fill={theme.palette.secondary.main} 
                            name="Interações"
                            radius={[4, 4, 0, 0]}
                          />
                        </BarChart>
                      </ResponsiveContainer>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              {/* Card do Gráfico de Créditos e Valor */}
              <Grid item xs={12} md={6}>
                <Card sx={{ height: '100%', border: '1px solid', borderColor: 'grey.200' }}>
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold', textAlign: 'center', mb: 2 }}>
                      Créditos e Valor (R$)
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                      <ResponsiveContainer width="100%" height={350}>
                        <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 40 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                          <XAxis 
                            dataKey="period"
                            tick={{ fontSize: 12 }}
                            tickLine={{ stroke: '#ccc' }}
                            angle={chartData.length > 6 ? -45 : 0}
                            textAnchor={chartData.length > 6 ? 'end' : 'middle'}
                            height={chartData.length > 6 ? 60 : 30}
                          />
                          <YAxis 
                            tick={{ fontSize: 12 }}
                            tickLine={{ stroke: '#ccc' }}
                          />
                          <RechartsTooltip 
                            contentStyle={{ 
                              backgroundColor: '#fff', 
                              border: '1px solid #ccc',
                              borderRadius: '8px',
                              boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                            }}
                          />
                          <Legend />
                          <Bar 
                            dataKey="creditos" 
                            fill={theme.palette.warning.main} 
                            name="Créditos"
                            radius={[4, 4, 0, 0]}
                          />
                          <Bar 
                            dataKey="valor" 
                            fill={theme.palette.success.main} 
                            name="Valor (R$)"
                            radius={[4, 4, 0, 0]}
                          />
                        </BarChart>
                      </ResponsiveContainer>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}
        </CardContent>
      </Card>

      {/* Snackbar para notificações */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </motion.div>
  );
};

export default AgentDiagnosticsV5;
